import { EntityType } from '@matech/thebigpos-sdk'
import { FixMeLater } from '../../../types'

export type FeatureValue = FixMeLater

export type FeaturesMapItem = {
	field: string
	label: string
	description?: string
	hideForSiteTypes?: EntityType[]
	dependentFields?: FeaturesMapItem[]
	value?: FeatureValue
	type?: 'string' | 'boolean'
	helperText?: string
}

export type DependentFieldsMap = Record<string, FeaturesMapItem>

export const DEPENDENT_FIELDS_MAP: DependentFieldsMap = {
	shortAppTitle: {
		field: 'shortAppTitle',
		label: 'Short App Title',
		type: 'string',
	},
	shortAppSubtitle: {
		field: 'shortAppSubtitle',
		label: 'Short App Subtitle',
		type: 'string',
	},
	fullAppTitle: {
		field: 'fullAppTitle',
		label: 'Full App Title',
		type: 'string',
	},
	fullAppSubtitle: {
		field: 'fullAppSubtitle',
		label: 'Full App Subtitle',
		type: 'string',
	},
	spanishPrequalTitle: {
		field: 'spanishPrequalTitle',
		label: 'Spanish Prequal Title',
		type: 'string',
	},
	spanishPrequalSubtitle: {
		field: 'spanishPrequalSubtitle',
		label: 'Spanish Prequal Subtitle',
		type: 'string',
	},
	spanishFullAppTitle: {
		field: 'spanishFullAppTitle',
		label: 'Spanish Full App Title',
		type: 'string',
	},
	spanishFullAppSubtitle: {
		field: 'spanishFullAppSubtitle',
		label: 'Spanish Full App Subtitle',
		type: 'string',
	},
	byPhoneTitle: {
		field: 'byPhoneTitle',
		label: 'By Phone Title',
		type: 'string',
	},
	byPhoneSubtitle: {
		field: 'byPhoneSubtitle',
		label: 'By Phone Subtitle',
		type: 'string',
	},
	loanStatus: {
		field: 'loanStatus',
		label: 'Loan Status',
		type: 'boolean',
	},
	loanAndProperty: {
		field: 'loanAndProperty',
		label: 'Loan and Property',
		type: 'boolean',
	},
	personalInformation: {
		field: 'personalInformation',
		label: 'Personal Information',
		type: 'boolean',
	},
	employment: {
		field: 'employment',
		label: 'Employment',
		type: 'boolean',
	},
	assets: {
		field: 'assets',
		label: 'Assets',
		type: 'boolean',
	},
	realEstate: {
		field: 'realEstate',
		label: 'Real Estate',
		type: 'boolean',
	},
	ccEmails: {
		field: 'ccEmails',
		label: 'CC Emails',
		type: 'string',
		helperText: 'Enter emails separated by a semicolon (;)',
	},
}

export type FeaturesMap = Record<string, FeaturesMapItem>

export const FEATURES_MAP: FeaturesMap = {
	leads: {
		field: 'leads',
		label: 'Leads',
		description: 'Show Leads Card on Dashboard',
		hideForSiteTypes: [],
	},
	leadApp: {
		field: 'leadApp',
		label: 'Lead App (Super Short App)',
		description:
			'The shortest Loan Application with minimal information',
		hideForSiteTypes: [],
	},
	mobilePrequal: {
		field: 'mobilePrequal',
		label: 'Prequal (Short App)',
		description:
			'Loan application with enough information to make a Pre-Qualification decision',
		hideForSiteTypes: [],
		dependentFields: [
			DEPENDENT_FIELDS_MAP.shortAppTitle,
			DEPENDENT_FIELDS_MAP.shortAppSubtitle,
		],
	},
	fullApp: {
		field: 'fullApp',
		label: 'Full App (Full URLA)',
		description:
			'The "Soup to Nuts" application with almost all of the URLA fields',
		hideForSiteTypes: [],
		dependentFields: [
			DEPENDENT_FIELDS_MAP.fullAppTitle,
			DEPENDENT_FIELDS_MAP.fullAppSubtitle,
		],
	},
	addCoBorrower: {
		field: 'addCoBorrower',
		label: 'Co-Borrower App',
		description:
			'Allow borrowers to add co-borrowers to their loan applications',
		hideForSiteTypes: [],
	},
	spanishPrequal: {
		field: 'spanishPrequal',
		label: 'Spanish Prequal (Short App)',
		description: 'Spanish version of the Prequal (Short App)',
		hideForSiteTypes: [],
		dependentFields: [
			DEPENDENT_FIELDS_MAP.spanishPrequalTitle,
			DEPENDENT_FIELDS_MAP.spanishPrequalSubtitle,
		],
	},
	spanishFullApp: {
		field: 'spanishFullApp',
		label: 'Spanish Full App (Full URLA)',
		description: 'Spanish version of the Full App (Full URLA)',
		hideForSiteTypes: [],
		dependentFields: [
			DEPENDENT_FIELDS_MAP.spanishFullAppTitle,
			DEPENDENT_FIELDS_MAP.spanishFullAppSubtitle,
		],
	},
	buyerClient: {
		field: 'buyerClient',
		label: 'Buyer Lead Form',
		description: 'Lead form for Buyer Clients',
		hideForSiteTypes: ['Corporate', 'Branch', 'LoanOfficer'],
	},
	sellerClient: {
		field: 'sellerClient',
		label: 'Seller Lead Form',
		description: 'Lead form for Seller clients',
		hideForSiteTypes: ['Corporate', 'Branch', 'LoanOfficer'],
	},
	clientLifestyle: {
		field: 'clientLifestyle',
		label: 'Client Lifestyle',
		description: 'Gather valuable information about your clients',
		hideForSiteTypes: ['Corporate', 'Branch', 'LoanOfficer'],
	},
	openHouseForm: {
		field: 'openHouseForm',
		label: 'Open House Form',
		description: 'Show open house check-in form',
		hideForSiteTypes: ['Corporate', 'Branch', 'LoanOfficer'],
	},
	listingOfferForm: {
		field: 'listingOfferForm',
		label: 'Listing Offer Form',
		description: 'Show listing offer form',
		hideForSiteTypes: ['Corporate', 'Branch', 'LoanOfficer'],
	},
	ringCentral: {
		field: 'ringCentral',
		label: 'Apply By Phone',
		description:
			'Display an icon for one-touch dialing to take applications by phone',
		hideForSiteTypes: ['Realtor'],
		dependentFields: [
			DEPENDENT_FIELDS_MAP.byPhoneTitle,
			DEPENDENT_FIELDS_MAP.byPhoneSubtitle,
		],
	},
	borrowerTasks: {
		field: 'borrowerTasks',
		label: 'Borrower Tasks',
		description: 'Display a list of Tasks for the user to complete',
		hideForSiteTypes: [],
	},
	documents: {
		field: 'documents',
		label: 'Documents',
		description: 'Show Document Vault',
		hideForSiteTypes: [],
	},
	loanCalculator: {
		field: 'loanCalculator',
		label: 'Loan Calculator',
		description:
			'Allow Loan Structuring for Loan Officers and Partners',
		hideForSiteTypes: [],
	},
	emailNotifications: {
		field: 'emailNotifications',
		label: 'Email Notifications',
		description:
			'Notify users when an action is performed or the changes are made',
		hideForSiteTypes: ['Realtor'],
		dependentFields: [DEPENDENT_FIELDS_MAP.ccEmails],
	},
	docusign: {
		field: 'docusign',
		label: 'Docusign',
		description:
			'Enables E-Signature tasks\n* Requires Docusign integration',
		hideForSiteTypes: ['Realtor'],
	},
	disclosures: {
		field: 'disclosures',
		label: 'Disclosures',
		description:
			'Enables Single Sign On (SSO) to Consumer Connect\n* Requires Consumer Connect connection',
		hideForSiteTypes: ['Realtor'],
	},
	payments: {
		field: 'payments',
		label: 'Payments',
		description:
			'Show a button to allow Borrowers to make first-time payments or pay a bill\n* Requires Finigree integration',
		hideForSiteTypes: ['Realtor'],
	},
	voc: {
		field: 'voc',
		label: 'VOC (Soft Pull)',
		description: 'Enabled Verification of Credit (VOC)',
		hideForSiteTypes: ['Realtor'],
	},
	socialSurvey: {
		field: 'socialSurvey',
		label: 'Experience.com Reviews',
		description:
			'Show reviews from Experience.com on landing pages\n* Requires Experience.com integration',
		hideForSiteTypes: ['Realtor'],
	},
	encompassWeb: {
		field: 'encompassWeb',
		label: 'Encompass Web',
		description: 'Enable Encompass Web Tile in Loan Officer Portal',
		hideForSiteTypes: ['Realtor'],
	},
	support: {
		field: 'support',
		label: 'Support',
		description: 'Enable Support Tile in Loan Officer Portal',
		hideForSiteTypes: ['Realtor'],
	},
	loanDetails: {
		field: 'loanDetails',
		label: 'Loan Details',
		description: 'Show loan details to borrowers.',
		hideForSiteTypes: [],
		dependentFields: [
			DEPENDENT_FIELDS_MAP.loanStatus,
			DEPENDENT_FIELDS_MAP.loanAndProperty,
			DEPENDENT_FIELDS_MAP.personalInformation,
			DEPENDENT_FIELDS_MAP.employment,
			DEPENDENT_FIELDS_MAP.assets,
			DEPENDENT_FIELDS_MAP.realEstate,
		],
	},
	listings: {
		field: 'listings',
		label: 'Listings',
		description: 'Show listings on the Dashboard',
		hideForSiteTypes: ['Account', 'Corporate', 'Branch'],
	},
	autoNameTaskDocuments: {
		field: 'autoNameTaskDocuments',
		label: 'Auto Name Task Documents',
		description:
			'Automatically name documents uploaded to Tasks based on the Task name',
		hideForSiteTypes: [],
	},
}

export const FEATURE_TITLE_BLACKLIST = [
	'createdAt',
	'updatedAt',
	'deletedAt',
	'id',
]
